/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { z } from 'zod';

import {
  COMMUNICATION_METHODS,
  CRY_AFTER_BIRTH,
  DELIVERY_TYPES,
  DRESSING_SKILLS,
  FOOD_TEXTURES,
  INTAKE_GENDERS,
  INTAKE_TRISTATE,
  MEAL_COMPLETION_TIMES,
  PREGNANCY_DURATIONS,
  SAFETY_AWARENESS,
  SELFCARE_LEVELS,
  SPECIAL_DIETS,
  TOILETING_SKILLS,
} from './intake-options';

/**
 * Patient-intake assessment validators — the single contract validated
 * identically by the parent RHF form (per section) and the API DTOs. The eight
 * sections mirror the BRD intake form 1:1; each section schema is exported so
 * the wizard can validate one step at a time, while {@link IntakeSubmitSchema}
 * validates the complete payload on final submit.
 */

/** Intake workflow status — mirrors the API `intake_status` enum. */
export const INTAKE_STATUSES = [
  'form_pending',
  'form_filled',
  'under_review',
  'scheduled',
  'arrived',
  'completed',
] as const;
export type IntakeStatus = (typeof INTAKE_STATUSES)[number];

const required = (msg = 'This field is required'): z.ZodString => z.string().trim().min(1, msg);
const isoDate = z
  .string()
  .trim()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a YYYY-MM-DD date');
// A 10-digit Indian mobile, with an optional +91 / 91 country prefix.
const phone = z
  .string()
  .trim()
  .regex(/^(?:\+?91)?\d{10}$/, 'Enter a valid 10-digit phone number');
const triState = z.enum(INTAKE_TRISTATE);
/** Per-skill checkbox grid (gross/fine motor) — only ticked skills are sent. */
const checkboxRecord = z.record(z.string(), z.boolean());

/** Section 1 — basic patient details + at least one guardian. */
export const GuardianSchema = z.object({
  name: required('Guardian name is required'),
  relationship: required('Relationship is required'),
  contact: z.string().trim().optional().or(z.literal('')),
  email: z.string().trim().email('Enter a valid email').optional().or(z.literal('')),
});
export const BasicDetailsSchema = z.object({
  firstName: required('First name is required'),
  lastName: required('Last name is required'),
  dateOfBirth: isoDate,
  gender: z.enum(INTAKE_GENDERS, { message: 'Select a gender' }),
  primaryLanguage: required('Primary language is required'),
  addressLine1: required('Address is required'),
  addressLine2: z.string().trim().optional(),
  city: required('City is required'),
  state: required('State is required'),
  pinCode: z.string().regex(/^\d{6}$/, 'Enter a valid 6-digit pin code'),
  primaryContact: phone,
  secondaryContact: phone.optional().or(z.literal('')),
  email: z.string().trim().email('Enter a valid email').optional().or(z.literal('')),
  guardians: z.array(GuardianSchema).min(1, 'Add at least one parent or guardian'),
});

/** Section 2 — birth & neonatal history. */
export const BirthHistorySchema = z.object({
  pregnancyDuration: z.enum(PREGNANCY_DURATIONS),
  deliveryType: z.enum(DELIVERY_TYPES),
  birthWeightKg: z.coerce.number().min(0.5, 'Minimum is 0.5 kg').max(6, 'Maximum is 6.0 kg'),
  criedAfterBirth: z.enum(CRY_AFTER_BIRTH),
  jaundiceHistory: triState,
  jaundiceDetails: z.string().trim().optional(),
  appearanceChanges: triState,
  appearanceDetails: z.string().trim().optional(),
});

/** Section 3 — post-natal history. */
export const PostNatalSchema = z.object({
  breastfeeding: triState,
  seizureHistory: triState,
  seizureDetails: z.string().trim().optional(),
  complicationsAfterBirth: triState,
  complicationDetails: z.string().trim().optional(),
});

/** Section 4 — developmental milestones (ages in months). */
export const MilestonesSchema = z.object({
  neckControlAge: z.coerce.number().int().min(1).max(24),
  didRoll: triState,
  didCrawl: triState,
  ageSat: z.coerce.number().int().min(1).max(36),
  ageStood: z.coerce.number().int().min(1).max(48),
  ageStartedRunning: z.coerce.number().int().min(1).max(60),
});

/** Section 5 — motor skills, health & safety. */
export const MotorSkillsSchema = z.object({
  gross: checkboxRecord,
  fine: checkboxRecord,
  allergies: z.string().trim().optional(),
  safety: z.record(z.string(), z.enum(SAFETY_AWARENESS)).optional(),
  specialDiet: z.enum(SPECIAL_DIETS).optional(),
  specialDietOther: z.string().trim().optional(),
});

/** Section 6 — daily living & feeding. */
export const DailyLivingSchema = z.object({
  feedsIndependently: triState,
  screenTimeDuringMeals: triState,
  mealCompletionTime: z.enum(MEAL_COMPLETION_TIMES),
  foodTextures: z.array(z.enum(FOOD_TEXTURES)).min(1, 'Select at least one'),
  drinksWaterAfterEachServing: triState,
  sleepingPatterns: required('Please describe sleeping patterns'),
  toiletingSkills: z.enum(TOILETING_SKILLS),
  brushingSkills: z.enum(SELFCARE_LEVELS),
  bathingSkills: z.enum(SELFCARE_LEVELS),
  dressingSkills: z.enum(DRESSING_SKILLS),
});

/** Section 7 — speech & language profile. */
export const SpeechLanguageSchema = z.object({
  communicationMethods: z.array(z.enum(COMMUNICATION_METHODS)).min(1, 'Select at least one'),
  meaningfulWordCount: z.coerce.number().int().min(0).max(10000),
  doesBabble: triState,
  canAskForThings: triState,
  asksForItems: z.string().trim().optional(),
  canLabelObjects: required('Please describe what your child can name'),
  imitatesSingleWords: triState,
  imitatesPhrases: triState,
  repeatsMemorizedSpeech: triState,
  memorizedSpeechDetails: z.string().trim().optional(),
  fillsBlanksInSongs: triState,
  songsList: z.string().trim().optional(),
});

/** Section 8 — preferences & therapy goals. */
export const PreferencesGoalsSchema = z.object({
  favoritesGamesRhymesFood: required("Please tell us about your child's preferences"),
  expectedChangesPostTherapy: required('Please share your therapy goals'),
});

/** The complete intake payload, validated on final submit. */
export const IntakeSubmitSchema = z.object({
  basicDetails: BasicDetailsSchema,
  birthHistory: BirthHistorySchema,
  postNatal: PostNatalSchema,
  milestones: MilestonesSchema,
  motorSkills: MotorSkillsSchema,
  dailyLiving: DailyLivingSchema,
  speechLanguage: SpeechLanguageSchema,
  preferencesGoals: PreferencesGoalsSchema,
});
export type IntakeSubmitInput = z.infer<typeof IntakeSubmitSchema>;

/**
 * Draft payload for auto-save / "save & continue later" — every section and
 * field is optional, so a half-finished form persists without tripping the
 * full-submit rules.
 */
export const IntakeDraftSchema = z.object({
  basicDetails: BasicDetailsSchema.partial().optional(),
  birthHistory: BirthHistorySchema.partial().optional(),
  postNatal: PostNatalSchema.partial().optional(),
  milestones: MilestonesSchema.partial().optional(),
  motorSkills: MotorSkillsSchema.partial().optional(),
  dailyLiving: DailyLivingSchema.partial().optional(),
  speechLanguage: SpeechLanguageSchema.partial().optional(),
  preferencesGoals: PreferencesGoalsSchema.partial().optional(),
});
export type IntakeDraftInput = z.infer<typeof IntakeDraftSchema>;

/**
 * Admin "schedule the intake appointment" payload — assign a therapist and book
 * a real session. `scheduledStart` is a full ISO timestamp (the FE converts its
 * datetime-local input via `toISOString()`).
 */
export const ScheduleIntakeSchema = z.object({
  therapistId: z.string().uuid('Select a therapist'),
  scheduledStart: z.string().datetime({ message: 'Pick a date and time' }),
});
export type ScheduleIntakeInput = z.infer<typeof ScheduleIntakeSchema>;

/**
 * Admin e-signature payload — a base64 PNG data URL captured in the browser. The
 * strict prefix + size cap keep it to an embeddable image (never script).
 */
export const SignIntakeSchema = z.object({
  signatureDataUrl: z
    .string()
    .regex(/^data:image\/png;base64,[A-Za-z0-9+/]+={0,2}$/, 'Provide a PNG signature')
    .max(2_000_000, 'Signature image is too large'),
});
export type SignIntakeInput = z.infer<typeof SignIntakeSchema>;

/** Section keys in display order — drives the FE wizard + the PDF section order. */
export const INTAKE_SECTIONS = [
  'basicDetails',
  'birthHistory',
  'postNatal',
  'milestones',
  'motorSkills',
  'dailyLiving',
  'speechLanguage',
  'preferencesGoals',
] as const;
export type IntakeSection = (typeof INTAKE_SECTIONS)[number];
